import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { Feature, FeatureCollection, Point } from "geojson";
import {
  Circle as CircleIcon,
  Copy,
  MapPin,
  MousePointer,
  Pentagon,
  Spline,
  Square,
  X,
} from "lucide-react";
import maplibregl, { type Map as MlMap, type StyleSpecification } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawFreehandMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawSelectMode,
} from "terra-draw";
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter";

import { entities as entitiesApi, signals as signalsApi } from "@/lib/api";
import type { ApiEntity } from "@/lib/api";
import { colorForEntity } from "@/lib/entity-color";
import { getDomainHints } from "@/lib/domain-hints";
import { useFocusSet } from "@/lib/use-focus";
import { useTimeRange } from "@/lib/use-time-range";
import { useWorkspaces } from "@/store/workspaces";
import { CreateZoneModal } from "./create-zone-modal";
import { EntityQuickCard } from "./entity-quick-card";
import { MapLegend, type LegendState, type LayerKey } from "./map-legend";

/**
 * LiveGeographicCanvas — MapLibre GL JS with OSINT-friendly basemaps.
 *
 * Entities are rendered as native MapLibre **GeoJSON layers** (circle + symbol),
 * not HTML markers, so positioning is handled by MapLibre's geographic pipeline.
 * That avoids the marker-drift class of bugs you get when CSS transforms on the
 * marker DOM compete with MapLibre's own positioning.
 *
 * **No API key required** for any of the defaults:
 *   - Dark:      Carto Dark Matter (OSM data + Carto styling)
 *   - Light:     OpenStreetMap standard raster
 *   - Satellite: ESRI World Imagery
 *
 * **Custom styles:** set `VITE_MAP_STYLE_URL` to any MapLibre/Mapbox-compatible
 * style URL (MapTiler, Stadia, Mapbox, etc.).
 */

type StyleId = "dark" | "light" | "satellite" | "custom";

const CUSTOM_STYLE = (import.meta.env.VITE_MAP_STYLE_URL as string | undefined)?.trim() || "";

// MapLibre demo glyph endpoint — used so our symbol/text layers can render
// labels on top of raster basemaps (raster styles don't ship glyphs).
const GLYPHS = "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf";

const CARTO_DARK: StyleSpecification = {
  version: 8,
  glyphs: GLYPHS,
  sources: {
    "carto-dark": {
      type: "raster",
      tiles: [
        "https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
        "https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
        "https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
        "https://d.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
      ],
      tileSize: 256,
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
    },
  },
  layers: [
    { id: "bg", type: "background", paint: { "background-color": "#0a0a0c" } },
    { id: "carto-dark", type: "raster", source: "carto-dark" },
  ],
};

const OSM_LIGHT: StyleSpecification = {
  version: 8,
  glyphs: GLYPHS,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    },
  },
  layers: [
    { id: "bg", type: "background", paint: { "background-color": "#ffffff" } },
    { id: "osm", type: "raster", source: "osm" },
  ],
};

const ESRI_SATELLITE: StyleSpecification = {
  version: 8,
  glyphs: GLYPHS,
  sources: {
    esri: {
      type: "raster",
      tiles: [
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
      attribution:
        'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
    },
    labels: {
      type: "raster",
      tiles: [
        "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
    },
  },
  layers: [
    { id: "bg", type: "background", paint: { "background-color": "#0a0a0c" } },
    { id: "esri", type: "raster", source: "esri" },
    { id: "labels", type: "raster", source: "labels" },
  ],
};

function styleFor(id: StyleId): string | StyleSpecification {
  if (id === "custom" && CUSTOM_STYLE) return CUSTOM_STYLE;
  if (id === "satellite") return ESRI_SATELLITE;
  if (id === "light") return OSM_LIGHT;
  return CARTO_DARK;
}

function entityLatLng(e: ApiEntity): [number, number] | null {
  const lat = e.geolocation?.lat ?? e.lat ?? null;
  const lng = e.geolocation?.lng ?? e.lng ?? null;
  if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return [lng, lat]; // GeoJSON / MapLibre order: [lng, lat]
}

interface MarkerGroup {
  coord: [number, number];
  entities: ApiEntity[];
}

function groupByCoord(located: { e: ApiEntity; coord: [number, number] }[]): MarkerGroup[] {
  const byKey = new Map<string, MarkerGroup>();
  for (const { e, coord } of located) {
    // ~11m precision at the equator — close enough to consider "same place"
    const key = `${coord[0].toFixed(4)},${coord[1].toFixed(4)}`;
    const existing = byKey.get(key);
    if (existing) existing.entities.push(e);
    else byKey.set(key, { coord, entities: [e] });
  }
  return [...byKey.values()];
}

function buildFeatureCollection(
  groups: MarkerGroup[],
  focusSet: Set<string>,
): FeatureCollection {
  return {
    type: "FeatureCollection",
    features: groups.map((g) => {
      const focused = g.entities.some((e) => focusSet.has(e.id));
      return {
        type: "Feature",
        geometry: { type: "Point", coordinates: g.coord },
        properties: {
          label: g.entities.map((e) => e.name).join("\n"),
          type: g.entities[0].type,
          color: colorForEntity(g.entities[0]),
          count: g.entities.length,
          // First entity id — the canvas opens its slide-over on click. For
          // multi-entity clusters the popup lists everyone, then clicking opens
          // the primary one. We refine to a picker later.
          entityId: g.entities[0].id,
          entityIds: g.entities.map((e) => e.id).join(","),
          entityNames: g.entities.map((e) => e.name).join(" · "),
          entityTypes: g.entities.map((e) => e.type).join(" · "),
          // 1.0 if this group has any focused entity OR if there's no active
          // focus (so default behavior is unchanged when nothing is focused).
          dim: focusSet.size > 0 && !focused ? 1 : 0,
        },
      };
    }),
  };
}

function buildHeatmapCollection(
  entities: ApiEntity[],
  signals: { related_entity_ids: string[] }[],
): FeatureCollection {
  // Index entities by id with their coordinates
  const coords = new Map<string, [number, number]>();
  for (const e of entities) {
    const c = entityLatLng(e);
    if (c) coords.set(e.id, c);
  }
  const features: Feature[] = [];
  for (const s of signals) {
    for (const eid of s.related_entity_ids ?? []) {
      const c = coords.get(eid);
      if (!c) continue;
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: c },
        properties: { weight: 1 },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

function buildAreaCollection(
  entities: ApiEntity[],
  focusSet: Set<string>,
): FeatureCollection {
  const features: Feature[] = [];
  for (const e of entities) {
    if (!e.operational_area) continue;
    features.push({
      type: "Feature",
      geometry: e.operational_area as Feature["geometry"],
      properties: {
        entityId: e.id,
        name: e.name,
        type: e.type,
        color: colorForEntity(e),
        dim: focusSet.size > 0 && !focusSet.has(e.id) ? 1 : 0,
      },
    });
  }
  return { type: "FeatureCollection", features };
}

const SOURCE_ID = "horizon-entities";
const SOURCE_AREAS = "horizon-areas";
const SOURCE_SIGNALS = "horizon-signals";
const LAYER_AREAS_FILL = "horizon-areas-fill";
const LAYER_AREAS_OUTLINE = "horizon-areas-outline";
const LAYER_ROUTES = "horizon-areas-routes";
const LAYER_HEATMAP = "horizon-heatmap";
const LAYER_HALO = "horizon-entities-halo";
const LAYER_DOT = "horizon-entities-dot";
const LAYER_COUNT = "horizon-entities-count";
const LAYER_LABEL = "horizon-entities-label";

function ensureLayers(
  map: MlMap,
  fc: FeatureCollection,
  areas: FeatureCollection,
  heatmapFc: FeatureCollection,
  labelColor: string,
) {
  // Operational areas — drawn UNDER everything else
  const areaSrc = map.getSource(SOURCE_AREAS) as maplibregl.GeoJSONSource | undefined;
  if (areaSrc) areaSrc.setData(areas);
  else map.addSource(SOURCE_AREAS, { type: "geojson", data: areas });

  if (!map.getLayer(LAYER_AREAS_FILL)) {
    map.addLayer({
      id: LAYER_AREAS_FILL,
      type: "fill",
      source: SOURCE_AREAS,
      // fill geometry only — lines are rendered by LAYER_ROUTES instead
      filter: ["any", ["==", ["geometry-type"], "Polygon"], ["==", ["geometry-type"], "MultiPolygon"]],
      paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": [
          "case",
          ["==", ["get", "dim"], 1],
          0.05,
          0.18,
        ],
      },
    });
  }
  if (!map.getLayer(LAYER_AREAS_OUTLINE)) {
    map.addLayer({
      id: LAYER_AREAS_OUTLINE,
      type: "line",
      source: SOURCE_AREAS,
      // Only outline polygons — actual LineString routes get the bolder treatment below
      filter: ["any", ["==", ["geometry-type"], "Polygon"], ["==", ["geometry-type"], "MultiPolygon"]],
      paint: {
        "line-color": ["get", "color"],
        "line-width": 1.5,
        "line-opacity": [
          "case",
          ["==", ["get", "dim"], 1],
          0.2,
          0.85,
        ],
      },
    });
  }
  if (!map.getLayer(LAYER_ROUTES)) {
    map.addLayer({
      id: LAYER_ROUTES,
      type: "line",
      source: SOURCE_AREAS,
      // Routes / corridors / supply lines — anything line-shaped on the map
      filter: [
        "any",
        ["==", ["geometry-type"], "LineString"],
        ["==", ["geometry-type"], "MultiLineString"],
      ],
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-color": ["get", "color"],
        "line-width": 3.5,
        "line-dasharray": [2, 1.5],
        "line-opacity": [
          "case",
          ["==", ["get", "dim"], 1],
          0.2,
          0.9,
        ],
      },
    });
  }

  // Signal heatmap — placed UNDER markers but OVER areas
  const heatSrc = map.getSource(SOURCE_SIGNALS) as maplibregl.GeoJSONSource | undefined;
  if (heatSrc) heatSrc.setData(heatmapFc);
  else map.addSource(SOURCE_SIGNALS, { type: "geojson", data: heatmapFc });
  if (!map.getLayer(LAYER_HEATMAP)) {
    map.addLayer({
      id: LAYER_HEATMAP,
      type: "heatmap",
      source: SOURCE_SIGNALS,
      maxzoom: 14,
      paint: {
        "heatmap-weight": [
          "interpolate",
          ["linear"],
          ["get", "weight"],
          0,
          0,
          5,
          1,
        ],
        "heatmap-intensity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          0,
          1,
          12,
          3.2,
        ],
        "heatmap-color": [
          "interpolate",
          ["linear"],
          ["heatmap-density"],
          0,
          "rgba(0,0,0,0)",
          0.2,
          "rgba(96,165,250,0.55)",
          0.45,
          "rgba(245,158,11,0.7)",
          0.7,
          "rgba(239,68,68,0.8)",
          1,
          "rgba(127,29,29,0.9)",
        ],
        "heatmap-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          0,
          12,
          12,
          40,
        ],
        "heatmap-opacity": 0.85,
      },
    });
  }

  // (Re)create or update the entities source
  const src = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
  if (src) {
    src.setData(fc);
  } else {
    map.addSource(SOURCE_ID, { type: "geojson", data: fc });
  }

  // Halo (glow behind the dot)
  if (!map.getLayer(LAYER_HALO)) {
    map.addLayer({
      id: LAYER_HALO,
      type: "circle",
      source: SOURCE_ID,
      paint: {
        "circle-radius": 13,
        "circle-color": ["get", "color"],
        "circle-opacity": ["case", ["==", ["get", "dim"], 1], 0.05, 0.25],
        "circle-blur": 0.6,
      },
    });
  }

  // Solid dot
  if (!map.getLayer(LAYER_DOT)) {
    map.addLayer({
      id: LAYER_DOT,
      type: "circle",
      source: SOURCE_ID,
      paint: {
        "circle-radius": 6,
        "circle-color": ["get", "color"],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1.5,
        "circle-opacity": ["case", ["==", ["get", "dim"], 1], 0.25, 1],
        "circle-stroke-opacity": ["case", ["==", ["get", "dim"], 1], 0.25, 1],
      },
    });
  }

  // Count badge — only when count > 1
  if (!map.getLayer(LAYER_COUNT)) {
    map.addLayer({
      id: LAYER_COUNT,
      type: "symbol",
      source: SOURCE_ID,
      filter: [">", ["get", "count"], 1],
      layout: {
        "text-field": ["to-string", ["get", "count"]],
        "text-font": ["Open Sans Bold"],
        "text-size": 10,
        "text-offset": [0.9, -0.9],
        "text-anchor": "center",
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": "#0a0a0c",
        "text-halo-color": "#ffffff",
        "text-halo-width": 2,
      },
    });
  }

  // Entity name label — always visible, anchored to the right of the dot
  if (!map.getLayer(LAYER_LABEL)) {
    map.addLayer({
      id: LAYER_LABEL,
      type: "symbol",
      source: SOURCE_ID,
      layout: {
        "text-field": ["get", "label"],
        "text-font": ["Open Sans Bold"],
        "text-size": 12,
        "text-anchor": "left",
        "text-offset": [1.1, 0],
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": labelColor,
        "text-halo-color": "rgba(0,0,0,0.9)",
        "text-halo-width": 1.5,
        "text-halo-blur": 0.5,
        "text-opacity": ["case", ["==", ["get", "dim"], 1], 0.25, 1],
      },
    });
  } else {
    map.setPaintProperty(LAYER_LABEL, "text-color", labelColor);
  }
}

type DrawMode =
  | "select"
  | "rectangle"
  | "polygon"
  | "circle"
  | "freehand";

type DrawnGeometry =
  | GeoJSON.Polygon
  | GeoJSON.MultiPolygon
  | GeoJSON.LineString
  | GeoJSON.MultiLineString;

type PendingZone = {
  // `null` = point of interest (no drawn shape); the modal then persists
  // only the centroid as the entity's lat/lng.
  geometry: DrawnGeometry | null;
  centroid: [number, number];
};

/** Right-click menu anchored to a specific map location. `lng`/`lat` are
 *  frozen at the moment of the click so the operator can act on an exact
 *  coordinate — the hover readout keeps tracking the cursor and is useless
 *  the instant you move toward a button. */
type MapMenu = { x: number; y: number; lng: number; lat: number };

export function LiveGeographicCanvas({ workspaceId }: { workspaceId: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const drawRef = useRef<TerraDraw | null>(null);
  // Refs mirror the marker-group array and the currently-pinned group so
  // the imperative MapLibre event handlers (registered once at mount) can
  // read the *current* values without going stale.
  const groupsRef = useRef<MarkerGroup[]>([]);
  const pinnedGroupRef = useRef<MarkerGroup | null>(null);
  const [tilesError, setTilesError] = useState<string | null>(null);
  const initialStyle: StyleId = CUSTOM_STYLE ? "custom" : "dark";
  const [styleId, setStyleId] = useState<StyleId>(initialStyle);
  // Live coordinates under the cursor. null when the pointer leaves the canvas.
  const [mouseCoords, setMouseCoords] = useState<{ lng: number; lat: number } | null>(null);
  const [copiedCoords, setCopiedCoords] = useState(false);
  // Which drawing tool is active. `select` means "no drawing, click to
  // select existing zones for editing."
  const [drawMode, setDrawMode] = useState<DrawMode>("select");
  // Set when terra-draw finishes a shape and the create-zone modal should
  // appear. Cleared on cancel or save.
  const [pendingZone, setPendingZone] = useState<PendingZone | null>(null);
  // Right-click menu on empty map space (copy exact coords / drop a POI).
  const [mapMenu, setMapMenu] = useState<MapMenu | null>(null);
  const [menuCopied, setMenuCopied] = useState(false);
  const navigate = useNavigate();
  const { getById } = useWorkspaces();
  const geoExample = getDomainHints(getById(workspaceId)?.domain).geographicExample;
  const tr = useTimeRange();

  // Geographic-only slice: only entities with lat/lng, and a much larger
  // window than the shared list default (500) — workspaces with heavy
  // actor ingestion (individual / armed_group / narrative etc.) would
  // otherwise fill the first 500 with location-less nodes and the map
  // would sit empty despite plenty of geographic entities existing further
  // down the ORDER BY. Separate queryKey so we don't clobber the Entity
  // list's cache (which needs the un-filtered set for edit / relate flows).
  const ents = useQuery({
    queryKey: ["entities", workspaceId, "with-coords"],
    queryFn: () =>
      entitiesApi.list(workspaceId, { hasCoords: true, limit: 5000 }),
    enabled: Boolean(workspaceId),
  });
  const sigs = useQuery({
    queryKey: ["signals", workspaceId],
    queryFn: () => signalsApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });

  const focusSet = useFocusSet();
  const [legend, setLegend] = useState<LegendState>({
    // Heatmap starts off — the marker layer is the default read of the map.
    hiddenLayers: new Set<LayerKey>(["heatmap"]),
    hiddenColors: new Set<string>(),
    hiddenSignalTypes: new Set<string>(),
  });
  // Quick-card pin: which entity marker is currently open, plus its live
  // screen-pixel position (updated on every map move so the card follows
  // as the operator pans/zooms). Cleared on close or when the marker
  // leaves the viewport.
  const [pinnedGroup, setPinnedGroup] = useState<MarkerGroup | null>(null);
  const [pinnedPixel, setPinnedPixel] = useState<{ x: number; y: number } | null>(null);
  // Filter entities up-front so every downstream collection respects the legend
  const visibleEntities = useMemo(() => {
    if (legend.hiddenColors.size === 0) return ents.data ?? [];
    return (ents.data ?? []).filter((e) => !legend.hiddenColors.has(colorForEntity(e)));
  }, [ents.data, legend.hiddenColors]);
  const located = useMemo(
    () =>
      visibleEntities
        .map((e) => ({ e, coord: entityLatLng(e) }))
        .filter((x): x is { e: ApiEntity; coord: [number, number] } => x.coord !== null),
    [visibleEntities],
  );
  const groups = useMemo(() => groupByCoord(located), [located]);
  // Keep the imperative-handler ref in sync — the click callback registered
  // on the MapLibre instance reads from this so it always sees the latest
  // grouping (a new signal-triggered rebuild shouldn't stale-out the card).
  useEffect(() => {
    groupsRef.current = groups;
    // If a card is currently pinned, refresh its group in state so entity
    // updates (edits, new siblings arriving at the same coord) reflect
    // immediately. Drop the pin if the coord no longer has any entities.
    const pinned = pinnedGroupRef.current;
    if (!pinned) return;
    const key = `${pinned.coord[0].toFixed(4)},${pinned.coord[1].toFixed(4)}`;
    const fresh = groups.find(
      (g) => `${g.coord[0].toFixed(4)},${g.coord[1].toFixed(4)}` === key,
    );
    if (fresh) setPinnedGroup(fresh);
    else {
      setPinnedGroup(null);
      setPinnedPixel(null);
    }
  }, [groups]);
  // Mirror pinned state to a ref so the map-move handler can read it.
  useEffect(() => {
    pinnedGroupRef.current = pinnedGroup;
  }, [pinnedGroup]);
  const featureCollection = useMemo(
    () => buildFeatureCollection(groups, focusSet),
    [groups, focusSet],
  );
  const areaCollection = useMemo(
    () => buildAreaCollection(visibleEntities, focusSet),
    [visibleEntities, focusSet],
  );
  const windowedSignals = useMemo(
    () =>
      tr.isActive
        ? (sigs.data ?? []).filter((s) => tr.inRange(s.timestamp))
        : (sigs.data ?? []),
    [sigs.data, tr],
  );
  // Type-count summary for the legend's signal-type filter. Sorted by count
  // desc so the operator sees the noisiest types first.
  const signalTypeSummary = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of windowedSignals) counts.set(s.type, (counts.get(s.type) ?? 0) + 1);
    return [...counts.entries()]
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);
  }, [windowedSignals]);
  // Apply the per-type filter *before* the heatmap collection is built so the
  // MapLibre layer only sees the visible signals — no extra work in ensureLayers.
  const filteredSignals = useMemo(
    () =>
      legend.hiddenSignalTypes.size === 0
        ? windowedSignals
        : windowedSignals.filter((s) => !legend.hiddenSignalTypes.has(s.type)),
    [windowedSignals, legend.hiddenSignalTypes],
  );
  const heatmapCollection = useMemo(
    () => buildHeatmapCollection(visibleEntities, filteredSignals),
    [visibleEntities, filteredSignals],
  );

  const labelColor = styleId === "light" ? "#111111" : "#f5f5f5";

  // Init map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: styleFor(initialStyle),
      center: [-74.3, 4.6], // Colombia centroid — sensible default for current showcase
      zoom: 5,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: false }), "bottom-right");
    map.addControl(
      new maplibregl.ScaleControl({ maxWidth: 100, unit: "metric" }),
      "bottom-left",
    );
    map.on("error", (e) => {
      if (e?.error?.message) setTilesError(e.error.message);
    });

    // Hover popup with all entity names at a coord
    map.on("mouseenter", LAYER_DOT, (e) => {
      map.getCanvas().style.cursor = "pointer";
      const f = e.features?.[0];
      if (!f) return;
      const coord = (f.geometry as Point).coordinates as [number, number];
      const names = String(f.properties?.entityNames ?? "");
      const types = String(f.properties?.entityTypes ?? "");
      const namesList = names.split(" · ");
      const typesList = types.split(" · ");
      const rows = namesList
        .map(
          (n, i) =>
            `<div style="margin-bottom:4px"><strong style="color:#fff">${escapeHtml(n)}</strong> <span style="color:#999">· ${escapeHtml(typesList[i] ?? "")}</span></div>`,
        )
        .join("");
      popupRef.current?.remove();
      popupRef.current = new maplibregl.Popup({
        offset: 14,
        closeButton: false,
        closeOnClick: false,
      })
        .setLngLat(coord)
        .setHTML(`<div style="font-size:11px; min-width:160px">${rows}</div>`)
        .addTo(map);
    });
    map.on("mouseleave", LAYER_DOT, () => {
      map.getCanvas().style.cursor = "";
      popupRef.current?.remove();
      popupRef.current = null;
    });

    // Click on a marker → pin the quick card. We look up the full group from
    // the ref so the card can show sibling entities that share the coord.
    // Areas and routes still route straight to the slide-over — those are
    // usually big geometries where a floating card doesn't add much.
    const openSlideOver = (id: string) => {
      navigate({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, entity: id }) });
    };
    map.on("click", LAYER_DOT, (e) => {
      const f = e.features?.[0];
      const id = f?.properties?.entityId as string | undefined;
      if (!id) return;
      const group = groupsRef.current.find((g) => g.entities.some((x) => x.id === id));
      if (!group) return;
      setPinnedGroup(group);
      const pt = map.project(group.coord);
      setPinnedPixel({ x: pt.x, y: pt.y });
    });
    map.on("click", LAYER_AREAS_FILL, (e) => {
      const id = e.features?.[0]?.properties?.entityId as string | undefined;
      if (id) openSlideOver(id);
    });
    map.on("click", LAYER_ROUTES, (e) => {
      const id = e.features?.[0]?.properties?.entityId as string | undefined;
      if (id) openSlideOver(id);
    });
    // Keep the pinned card glued to its marker as the map moves. Reading the
    // group from the ref avoids stale-closure bugs when groups change while
    // a card is open. MapLibre fires `move` on every animation frame during a
    // pan/zoom — calling setState directly there would rerender the whole
    // canvas per frame. We coalesce to at most one update per frame with rAF
    // and skip entirely when no card is pinned (the common case).
    let rafId: number | null = null;
    const syncPinnedPixel = () => {
      if (!pinnedGroupRef.current) return;
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const grp = pinnedGroupRef.current;
        if (!grp) return;
        const pt = map.project(grp.coord);
        setPinnedPixel({ x: pt.x, y: pt.y });
      });
    };
    map.on("move", syncPinnedPixel);
    map.on("zoom", syncPinnedPixel);
    map.on("resize", syncPinnedPixel);
    // Pointer cursor over interactive layers
    for (const lid of [LAYER_AREAS_FILL, LAYER_ROUTES]) {
      map.on("mouseenter", lid, () => (map.getCanvas().style.cursor = "pointer"));
      map.on("mouseleave", lid, () => (map.getCanvas().style.cursor = ""));
    }

    // Live coordinates under the cursor.
    //
    // We deliberately do NOT clear on `mouseout`. The chip is an overlay
    // inside the map container, so moving the pointer toward it fires
    // mouseout on the canvas — the old code nulled the coords, the chip
    // unmounted, and clicking "copy" was impossible (you were chasing a
    // element that vanished as you approached it). Keeping the last
    // position is also the conventional GIS readout behavior.
    map.on("mousemove", (e) => {
      setMouseCoords({ lng: e.lngLat.lng, lat: e.lngLat.lat });
    });

    // Right-click anywhere on the map → menu pinned to that exact location.
    // `e.point` is already in canvas pixel space, which is the same
    // coordinate system as the absolutely-positioned overlays.
    map.on("contextmenu", (e) => {
      // MapMouseEvent.preventDefault() only cancels *MapLibre's* own default
      // handling — it does not touch the DOM event, so the browser's native
      // context menu would still open on top of ours. Suppress both.
      e.preventDefault();
      e.originalEvent?.preventDefault();
      setMapMenu({
        x: e.point.x,
        y: e.point.y,
        lng: e.lngLat.lng,
        lat: e.lngLat.lat,
      });
    });

    // --- Drawing tools (terra-draw) ---
    // We instantiate ONCE with all modes registered, then flip between
    // them via setMode from the toolbar. `finish` fires when the user
    // completes a shape (double-click on polygon, click-release on
    // rectangle/circle, mouseup on freehand). We snapshot the geometry,
    // pop the create-zone modal, then clear the transient drawing so the
    // shape only becomes real once the operator saves it.
    const draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map, coordinatePrecision: 6 }),
      modes: [
        new TerraDrawSelectMode(),
        new TerraDrawRectangleMode(),
        new TerraDrawPolygonMode(),
        new TerraDrawCircleMode(),
        new TerraDrawFreehandMode(),
      ],
    });
    // terra-draw registers its own sources/layers on start(), which MapLibre
    // rejects with "Style is not done loading." if the basemap style hasn't
    // finished loading yet. On a fresh page load with real network latency
    // the raster style is still loading at this point, so calling start()
    // synchronously here threw and crashed the whole canvas (the error
    // bubbled out of the init effect). Gate it on style readiness: start
    // immediately only if the style is already loaded, otherwise wait for
    // the one-shot `load` event.
    const startDrawing = () => {
      try {
        draw.start();
        draw.setMode("select");
      } catch {
        /* map torn down before load fired — ignore */
      }
    };
    if (map.isStyleLoaded()) startDrawing();
    else map.once("load", startDrawing);
    draw.on("finish", (id) => {
      const snapshot = draw.getSnapshot();
      const feature = snapshot.find((f) => f.id === id);
      if (!feature) return;
      const geometry = feature.geometry as DrawnGeometry;
      const centroid = computeCentroid(geometry);
      // Clear the in-progress feature — it becomes real only if the modal
      // saves it. Leaves the map clean if the operator cancels.
      draw.clear();
      setPendingZone({ geometry, centroid });
      // Return to select mode so the user isn't stuck drawing shape after
      // shape without confirming the first one.
      draw.setMode("select");
      setDrawMode("select");
    });
    drawRef.current = draw;

    mapRef.current = map;
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      popupRef.current?.remove();
      try {
        drawRef.current?.stop();
      } catch {
        /* terra-draw throws if the map is already unmounted; ignore */
      }
      drawRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, [initialStyle]);

  // React to drawMode changes from the toolbar buttons. Terra-draw ignores
  // unknown mode names, so this is a safe funnel.
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw) return;
    try {
      draw.setMode(drawMode);
    } catch {
      /* mode not registered — should not happen with our fixed list */
    }
  }, [drawMode]);

  // Escape cancels the current drawing and returns to select mode. Useful
  // when the operator picks the wrong tool or wants to abort mid-polygon.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        drawRef.current?.clear();
        setDrawMode("select");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Swap basemap style. Layers/sources are owned by the style, so we must
  // re-add ours after `styledata` fires. Terra-draw's overlay layers are
  // also style-owned — we stop + restart it under the same event so the
  // drawing tools survive a basemap switch. Without this, activating a
  // draw mode after a basemap change would silently no-op.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    setTilesError(null);
    const onStyleLoad = () => {
      ensureLayers(map, featureCollection, areaCollection, heatmapCollection, labelColor);
      const draw = drawRef.current;
      if (draw) {
        try {
          draw.stop();
          draw.start();
          draw.setMode(drawMode);
        } catch {
          /* terra-draw may throw during style transitions — ignore */
        }
      }
    };
    map.setStyle(styleFor(styleId));
    map.once("styledata", onStyleLoad);
  }, [styleId]);

  // Push new data + fit bounds when entities change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      ensureLayers(map, featureCollection, areaCollection, heatmapCollection, labelColor);
      const bounds = new maplibregl.LngLatBounds();
      for (const g of groups) bounds.extend(g.coord);
      if (groups.length > 1) {
        map.fitBounds(bounds, { padding: 80, duration: 600, maxZoom: 9 });
      } else if (groups.length === 1) {
        map.flyTo({ center: groups[0].coord, zoom: 8, duration: 600 });
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
  }, [featureCollection, areaCollection, heatmapCollection, groups, labelColor]);

  // Apply layer visibility from the legend
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const vis = (hide: boolean) => (hide ? "none" : "visible");
      const hideMarkers = legend.hiddenLayers.has("markers");
      const hideAreas = legend.hiddenLayers.has("areas");
      const hideRoutes = legend.hiddenLayers.has("routes");
      const hideLabels = legend.hiddenLayers.has("labels");
      const hideHeatmap = legend.hiddenLayers.has("heatmap");
      for (const [id, hide] of [
        [LAYER_HALO, hideMarkers],
        [LAYER_DOT, hideMarkers],
        [LAYER_COUNT, hideMarkers],
        [LAYER_LABEL, hideLabels],
        [LAYER_AREAS_FILL, hideAreas],
        [LAYER_AREAS_OUTLINE, hideAreas],
        [LAYER_ROUTES, hideRoutes],
        [LAYER_HEATMAP, hideHeatmap],
      ] as [string, boolean][]) {
        if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", vis(hide));
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once("idle", apply);
  }, [legend.hiddenLayers, styleId]);

  const totalEntities = ents.data?.length ?? 0;
  const totalSignals = windowedSignals.length;
  const missingCoords = totalEntities - located.length;

  return (
    <div className="relative h-full w-full overflow-hidden rounded-md bg-[oklch(0.10_0.01_260)] flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-surface/60 text-[11px]">
        <div className="flex items-center gap-4 text-foreground/80">
          <span>
            <span className="font-mono text-foreground">{located.length}</span>{" "}
            <span className="text-muted-foreground">mapped</span>
          </span>
          {missingCoords > 0 && (
            <span className="text-muted-foreground">
              {missingCoords} entit{missingCoords === 1 ? "y" : "ies"} without coords
            </span>
          )}
          <span className="text-muted-foreground">{totalSignals} signals</span>
        </div>
        <StyleSwitcher
          styleId={styleId}
          onChange={setStyleId}
          customAvailable={Boolean(CUSTOM_STYLE)}
        />
      </div>

      <div ref={containerRef} className="relative flex-1 min-h-0">
        <MapLegend
          entities={ents.data ?? []}
          signalTypes={signalTypeSummary}
          state={legend}
          onChange={setLegend}
        />
        <DrawingToolbar mode={drawMode} onChange={setDrawMode} />
        {pinnedGroup && pinnedPixel && (
          <EntityQuickCard
            workspaceId={workspaceId}
            entity={pinnedGroup.entities[0]}
            entitiesAtCoord={pinnedGroup.entities}
            screenX={pinnedPixel.x}
            screenY={pinnedPixel.y}
            onClose={() => {
              setPinnedGroup(null);
              setPinnedPixel(null);
            }}
          />
        )}
        <CoordinatesChip
          coords={mouseCoords}
          copied={copiedCoords}
          onCopy={async () => {
            if (!mouseCoords) return;
            try {
              await navigator.clipboard.writeText(
                `${mouseCoords.lat.toFixed(6)}, ${mouseCoords.lng.toFixed(6)}`,
              );
              setCopiedCoords(true);
              setTimeout(() => setCopiedCoords(false), 1200);
            } catch {
              /* clipboard blocked — silent */
            }
          }}
        />
        {mapMenu && (
          <MapContextMenu
            menu={mapMenu}
            copied={menuCopied}
            onCopy={async () => {
              try {
                await navigator.clipboard.writeText(
                  `${mapMenu.lat.toFixed(6)}, ${mapMenu.lng.toFixed(6)}`,
                );
                setMenuCopied(true);
                setTimeout(() => {
                  setMenuCopied(false);
                  setMapMenu(null);
                }, 900);
              } catch {
                setMapMenu(null);
              }
            }}
            onAddPoint={() => {
              // geometry === null tells CreateZoneModal to persist a point
              // of interest at this exact coordinate.
              setPendingZone({ geometry: null, centroid: [mapMenu.lng, mapMenu.lat] });
              setMapMenu(null);
            }}
            onClose={() => setMapMenu(null)}
          />
        )}
        {pendingZone && (
          <CreateZoneModal
            workspaceId={workspaceId}
            geometry={pendingZone.geometry}
            centroid={pendingZone.centroid}
            onClose={() => setPendingZone(null)}
            onCreated={() => setPendingZone(null)}
          />
        )}
      </div>

      {tilesError && (
        <div className="absolute left-3 bottom-12 z-30 max-w-md rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-[11px] text-risk-high">
          Tile error: {tilesError}
        </div>
      )}

      {totalEntities > 0 && located.length === 0 && (
        <div className="absolute inset-0 grid place-items-center text-center px-6 pointer-events-none">
          <div className="max-w-md rounded-md border border-border bg-surface/95 p-4 pointer-events-auto">
            <div className="label-eyebrow mb-2">No coordinates yet</div>
            <p className="text-[12px] text-muted-foreground">
              You have {totalEntities} entit{totalEntities === 1 ? "y" : "ies"} but none with lat/lng.
              Ask the chat:{" "}
              <span className="text-foreground/85 italic">{geoExample}</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function StyleSwitcher({
  styleId,
  onChange,
  customAvailable,
}: {
  styleId: StyleId;
  onChange: (id: StyleId) => void;
  customAvailable: boolean;
}) {
  const Btn = ({ id, label }: { id: StyleId; label: string }) => (
    <button
      onClick={() => onChange(id)}
      className={`px-2 py-0.5 rounded transition ${
        styleId === id
          ? "bg-primary/20 text-primary"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex items-center gap-1 text-[11px]">
      <span className="text-muted-foreground mr-1">Basemap</span>
      <Btn id="dark" label="Dark" />
      <Btn id="light" label="Light" />
      <Btn id="satellite" label="Satellite" />
      {customAvailable && <Btn id="custom" label="Custom" />}
    </div>
  );
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c,
  );
}

// ---------- Drawing toolbar ----------

function DrawingToolbar({
  mode,
  onChange,
}: {
  mode: DrawMode;
  onChange: (mode: DrawMode) => void;
}) {
  const buttons: { id: DrawMode; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
    { id: "select", label: "Select / pan (Esc)", Icon: MousePointer },
    { id: "rectangle", label: "Draw rectangle", Icon: Square },
    // Distinct icons for polygon (n-gon) vs freehand (curved line) so the
    // toolbar reads at a glance without hovering for tooltips.
    { id: "polygon", label: "Draw polygon (click vertices, double-click to finish)", Icon: Pentagon },
    { id: "circle", label: "Draw circle", Icon: CircleIcon },
    { id: "freehand", label: "Freehand (click and drag)", Icon: Spline },
  ];
  return (
    <div className="absolute left-3 top-3 z-10 flex flex-col gap-1 rounded-md border border-border bg-surface/95 backdrop-blur p-1 shadow-md">
      <div className="label-eyebrow px-1.5 pt-0.5 pb-0.5">Draw</div>
      {buttons.map(({ id, label, Icon }) => {
        const active = mode === id;
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            title={label}
            aria-label={label}
            aria-pressed={active}
            className={`grid h-7 w-7 place-items-center rounded transition ${
              active
                ? "bg-primary/20 text-primary border border-primary/30"
                : "text-muted-foreground hover:text-foreground hover:bg-surface-elevated"
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        );
      })}
      {mode !== "select" && (
        <button
          onClick={() => onChange("select")}
          title="Cancel drawing"
          className="mt-1 grid h-7 w-7 place-items-center rounded text-muted-foreground hover:text-risk-high hover:bg-risk-high/10"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ---------- Right-click map menu ----------

/**
 * Menu anchored to the exact location that was right-clicked. The frozen
 * lng/lat is what makes this usable: the hover readout tracks the cursor,
 * so by the time you reach a button the coordinate has already changed.
 */
function MapContextMenu({
  menu,
  copied,
  onCopy,
  onAddPoint,
  onClose,
}: {
  menu: MapMenu;
  copied: boolean;
  onCopy: () => void;
  onAddPoint: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Clamp inside the map container before paint (same approach as the entity
  // quick card) so a right-click near an edge doesn't render off-screen.
  useEffect(() => {
    const el = ref.current;
    const container = el?.parentElement;
    if (!el || !container) return;
    const w = el.offsetWidth || 210;
    const h = el.offsetHeight || 96;
    let left = menu.x + 4;
    let top = menu.y + 4;
    if (left + w > container.clientWidth - 8) left = menu.x - w - 4;
    if (top + h > container.clientHeight - 8) top = menu.y - h - 4;
    setPos({ left: Math.max(8, left), top: Math.max(8, top) });
  }, [menu.x, menu.y]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const onDown = (e: MouseEvent) => {
      if (ref.current?.contains(e.target as globalThis.Node)) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    const t = window.setTimeout(() => window.addEventListener("mousedown", onDown), 0);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
      window.removeEventListener("mousedown", onDown);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute z-40 w-[212px] overflow-hidden rounded-md border border-border bg-popover shadow-xl py-1"
      style={{
        left: `${pos?.left ?? menu.x + 4}px`,
        top: `${pos?.top ?? menu.y + 4}px`,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      <div className="px-3 py-1.5 border-b border-border/70">
        <div className="label-eyebrow !mb-0.5">Coordinates</div>
        <div className="font-mono text-[11px] text-foreground/90">
          {menu.lat.toFixed(6)}, {menu.lng.toFixed(6)}
        </div>
      </div>
      <button
        onClick={onCopy}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-[12px] text-left text-foreground/90 hover:bg-surface-elevated"
      >
        <Copy className={`h-3.5 w-3.5 shrink-0 ${copied ? "text-primary" : ""}`} />
        <span>{copied ? "Copied" : "Copy coordinates"}</span>
      </button>
      <button
        onClick={onAddPoint}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-[12px] text-left text-primary hover:bg-surface-elevated"
      >
        <MapPin className="h-3.5 w-3.5 shrink-0" />
        <span>Add point of interest</span>
      </button>
    </div>
  );
}

// ---------- Coordinates chip ----------

function CoordinatesChip({
  coords,
  copied,
  onCopy,
}: {
  coords: { lng: number; lat: number } | null;
  copied: boolean;
  onCopy: () => void;
}) {
  if (!coords) return null;
  return (
    <button
      onClick={onCopy}
      title={copied ? "Copied to clipboard" : "Click to copy lat, lng"}
      className="absolute left-3 bottom-24 z-10 inline-flex items-center gap-1.5 rounded-md border border-border bg-surface/95 backdrop-blur px-2 py-1 text-[11px] font-mono text-foreground/85 hover:text-foreground hover:border-primary/40 shadow-md"
    >
      <span>
        {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}
      </span>
      <Copy className={`h-3 w-3 ${copied ? "text-primary" : "text-muted-foreground"}`} />
    </button>
  );
}

// ---------- Centroid helper for zone creation ----------

/**
 * Rough centroid of any GeoJSON geometry — mean of vertex coords. Not the
 * true centroid of a complex polygon (that requires the shoelace-formula
 * area weighting), but good enough for "fly-to this zone when it's opened
 * later." Zones are typically small enough that the mean-of-vertices
 * approximation is visually indistinguishable from the true centroid.
 */
function computeCentroid(
  geometry:
    | GeoJSON.Polygon
    | GeoJSON.MultiPolygon
    | GeoJSON.LineString
    | GeoJSON.MultiLineString,
): [number, number] {
  const points: [number, number][] = [];
  const collect = (coords: unknown): void => {
    if (
      Array.isArray(coords) &&
      coords.length === 2 &&
      typeof coords[0] === "number" &&
      typeof coords[1] === "number"
    ) {
      points.push([coords[0], coords[1]]);
      return;
    }
    if (Array.isArray(coords)) coords.forEach(collect);
  };
  collect(geometry.coordinates as unknown);
  if (points.length === 0) return [0, 0];
  const sum = points.reduce(
    (acc, [x, y]) => [acc[0] + x, acc[1] + y],
    [0, 0] as [number, number],
  );
  return [sum[0] / points.length, sum[1] / points.length];
}
